import { z } from "zod"
import { MarketingChannelEnum } from "../customers/dtos"

export const ChannelEnum = {
  Whatsapp: "Whatsapp",
  Email: "Email",
  Web: "Web",
  Instagram: "Instagram",
  Gmail: "Gmail",
  Api: "Api",
  Outlook: "Outlook",
} as const

export type Channel = keyof typeof ChannelEnum

export const EmailConversationChannels = [
  ChannelEnum.Email,
  ChannelEnum.Gmail,
  ChannelEnum.Outlook,
] as const

export type EmailConversationChannel = (typeof EmailConversationChannels)[number]

export const isEmailConversationChannel = (
  channel: string | null | undefined,
): channel is EmailConversationChannel =>
  EmailConversationChannels.includes(channel as EmailConversationChannel)

/**
 * Channel schema for response data. Uses preprocess so that legacy "Email"
 * values stored in the DB are coerced to null instead of failing validation.
 * The unknown input type also satisfies the `satisfies z.input<>` check in
 * router files where the source type is the wider Prisma Channel enum.
 */
export const channelResponseSchema = z.preprocess(
  (val) => (Object.values(ChannelEnum).includes(val as Channel) ? val : null),
  z.enum(ChannelEnum).nullable(),
)

export const CampaignLogKindEnum = {
  Launch: "Launch",
  Trigger: "Trigger",
  TriggerDelayed: "TriggerDelayed",
  TriggerResumed: "TriggerResumed",
  FilterOut: "FilterOut",
  Outreach: "Outreach",
  ConversationAchieved: "ConversationAchieved",
  WorkflowActionFailed: "WorkflowActionFailed",
  ConditionsNotMet: "ConditionsNotMet",
  WorkflowExecutionSucceeded: "WorkflowExecutionSucceeded",
  WorkflowExecutionFailed: "WorkflowExecutionFailed",
} as const

export type CampaignLogKind = keyof typeof CampaignLogKindEnum

export const CampaignTriggerTypeEnum = {
  Webhook: "Webhook",
  QR: "QR",
  Link: "Link",
  Widget: "Widget",
  Klaviyo: "Klaviyo",
  InstagramDirectMessage: "InstagramDirectMessage",
  InstagramCommentReply: "InstagramCommentReply",
  WhatsappDefault: "WhatsappDefault",
  EmailInbox: "EmailInbox",
  Shopify: "Shopify",
  BigBlue: "BigBlue",
  Manual: "Manual",
} as const

export type CampaignTriggerType = keyof typeof CampaignTriggerTypeEnum

export const EditableCampaignTriggerTypeEnum = {
  Webhook: CampaignTriggerTypeEnum.Webhook,
  QR: CampaignTriggerTypeEnum.QR,
  Link: CampaignTriggerTypeEnum.Link,
  Widget: CampaignTriggerTypeEnum.Widget,
  InstagramDirectMessage: CampaignTriggerTypeEnum.InstagramDirectMessage,
  InstagramCommentReply: CampaignTriggerTypeEnum.InstagramCommentReply,
  WhatsappDefault: CampaignTriggerTypeEnum.WhatsappDefault,
  EmailInbox: CampaignTriggerTypeEnum.EmailInbox,
  Shopify: CampaignTriggerTypeEnum.Shopify,
  BigBlue: CampaignTriggerTypeEnum.BigBlue,
  Manual: CampaignTriggerTypeEnum.Manual,
} as const

export type EditableCampaignTriggerType = keyof typeof EditableCampaignTriggerTypeEnum

/**
 * Maps each communication channel to the triggers that can deliver
 * conversations on it. Array order is the recommended display order.
 * Email is intentionally excluded — it is a legacy value replaced by Gmail.
 */
export const ChannelTriggersMap = {
  [ChannelEnum.Whatsapp]: [
    CampaignTriggerTypeEnum.WhatsappDefault,
    CampaignTriggerTypeEnum.QR,
    CampaignTriggerTypeEnum.Link,
    CampaignTriggerTypeEnum.Widget,
    CampaignTriggerTypeEnum.Manual,
    CampaignTriggerTypeEnum.Webhook,
    CampaignTriggerTypeEnum.Shopify,
    CampaignTriggerTypeEnum.BigBlue,
  ],
  [ChannelEnum.Web]: [
    CampaignTriggerTypeEnum.QR,
    CampaignTriggerTypeEnum.Link,
    CampaignTriggerTypeEnum.Widget,
  ],
  [ChannelEnum.Instagram]: [
    CampaignTriggerTypeEnum.InstagramDirectMessage,
    CampaignTriggerTypeEnum.InstagramCommentReply,
  ],
  [ChannelEnum.Gmail]: [
    CampaignTriggerTypeEnum.EmailInbox,
    CampaignTriggerTypeEnum.Manual,
    CampaignTriggerTypeEnum.Webhook,
    CampaignTriggerTypeEnum.Shopify,
    CampaignTriggerTypeEnum.BigBlue,
  ],
  [ChannelEnum.Api]: [CampaignTriggerTypeEnum.Webhook],
  [ChannelEnum.Outlook]: [
    CampaignTriggerTypeEnum.EmailInbox,
    CampaignTriggerTypeEnum.Manual,
    CampaignTriggerTypeEnum.Webhook,
    CampaignTriggerTypeEnum.Shopify,
    CampaignTriggerTypeEnum.BigBlue,
  ],
} as const satisfies Record<Exclude<Channel, "Email">, ReadonlyArray<EditableCampaignTriggerType>>

export const InstagramVerifiedOptions = z.enum(["nonVerified", "verified", "all"])

export const OpeningEmailSchema = z.record(
  z.string(),
  z.object({ subject: z.string().optional(), body: z.string().optional() }),
)

export const EmailCampaignChannelIntegrationTypeEnum = {
  Gmail: "gmail",
  Outlook: "outlook",
} as const

const GmailCampaignTriggerIntegrationSchema = z.object({
  type: z.literal(EmailCampaignChannelIntegrationTypeEnum.Gmail),
  gmailIntegrationId: z.string().optional(),
  gmailIntegrationIds: z.array(z.string()).optional(),
  delayInDays: z.number().int().positive().nullish(),
  openingEmail: OpeningEmailSchema.nullish(),
  openingEmailLocaleOrder: z.array(z.string()).nullish(),
})

const OutlookCampaignTriggerIntegrationSchema = z.object({
  type: z.literal(EmailCampaignChannelIntegrationTypeEnum.Outlook),
  outlookIntegrationId: z.string().optional(),
  outlookIntegrationIds: z.array(z.string()).optional(),
  delayInDays: z.number().int().positive().nullish(),
  openingEmail: OpeningEmailSchema.nullish(),
  openingEmailLocaleOrder: z.array(z.string()).nullish(),
})

export const EmailCampaignChannelIntegrationSchema = z.discriminatedUnion("type", [
  GmailCampaignTriggerIntegrationSchema.pick({
    type: true,
    gmailIntegrationId: true,
    gmailIntegrationIds: true,
    delayInDays: true,
    openingEmail: true,
    openingEmailLocaleOrder: true,
  }),
  OutlookCampaignTriggerIntegrationSchema.pick({
    type: true,
    outlookIntegrationId: true,
    outlookIntegrationIds: true,
    delayInDays: true,
    openingEmail: true,
    openingEmailLocaleOrder: true,
  }),
])

export const ShopifyCampaignTriggerEnum = {
  AbandonCheckout: "AbandonCheckout",
  OrderFulfilled: "OrderFulfilled",
} as const

export type ShopifyCampaignTrigger =
  (typeof ShopifyCampaignTriggerEnum)[keyof typeof ShopifyCampaignTriggerEnum]

export const BigBlueCampaignTriggerEnum = {
  Backorder: "Backorder",
  Exception: "Exception",
  CarrierReturn: "CarrierReturn",
} as const

export type BigBlueCampaignTrigger =
  (typeof BigBlueCampaignTriggerEnum)[keyof typeof BigBlueCampaignTriggerEnum]

export const BigBlueBackorderSkuMatchStrategyEnum = {
  AtLeastOne: "atLeastOne",
  All: "all",
} as const

export const BigBlueBackorderConfigSchema = z.object({
  incomingThreshold: z.number().finite().int().nonnegative(),
  skuMatchStrategy: z.enum(BigBlueBackorderSkuMatchStrategyEnum),
})

export const BIGBLUE_BACKORDER_DEFAULT_CONFIG = {
  incomingThreshold: 5,
  skuMatchStrategy: BigBlueBackorderSkuMatchStrategyEnum.AtLeastOne,
} as const satisfies z.infer<typeof BigBlueBackorderConfigSchema>

// The integration a campaign's trigger is bound to.
// fields valid for that integration — the account id(s) plus any trigger-specific config. Shared by
// the update payload (where `null` means "unlink the current trigger integration") and the campaign
// response. Account ids are strings on both sides. Gmail keeps `gmailIntegrationId` for legacy
// single-account payloads and responses while new clients should use `gmailIntegrationIds`.
export const CampaignTriggerIntegrationSchema = z.discriminatedUnion("type", [
  GmailCampaignTriggerIntegrationSchema,
  OutlookCampaignTriggerIntegrationSchema,
  z.object({
    type: z.literal("instagramDirectMessage"),
    instagramIntegrationId: z.string(),
    audience: z
      .object({
        verified: InstagramVerifiedOptions.nullish(),
        minFollowersCount: z.number().nullish(),
        maxFollowersCount: z.number().nullish(),
      })
      .nullish(),
    delayInDays: z.number().int().positive().nullish(),
    discardPhrases: z.array(z.string()).nullish(),
  }),
  z.object({
    type: z.literal("instagramCommentReply"),
    instagramIntegrationId: z.string(),
    replies: z.array(z.string()).nullish(),
    allowList: z.array(z.string()).nullish(),
  }),
  z.object({ type: z.literal("qr"), qrId: z.string() }),
  z.object({
    type: z.literal("shopify"),
    trigger: z.enum(ShopifyCampaignTriggerEnum),
    delayInDays: z.number().int().nonnegative().nullish(),
    email: EmailCampaignChannelIntegrationSchema.nullish(),
  }),
  z.object({
    type: z.literal("bigblue"),
    trigger: z.enum(BigBlueCampaignTriggerEnum),
    backorder: BigBlueBackorderConfigSchema.optional(),
    email: EmailCampaignChannelIntegrationSchema.nullish(),
  }),
])

type CampaignTriggerIntegration = z.infer<typeof CampaignTriggerIntegrationSchema>
type EmailCampaignChannelIntegration = z.infer<typeof EmailCampaignChannelIntegrationSchema>

export function getEmailCampaignChannelIntegration(
  triggerIntegration: CampaignTriggerIntegration | null | undefined,
): EmailCampaignChannelIntegration | undefined {
  if (!triggerIntegration) return undefined

  switch (triggerIntegration.type) {
    case EmailCampaignChannelIntegrationTypeEnum.Gmail:
    case EmailCampaignChannelIntegrationTypeEnum.Outlook:
      return triggerIntegration
    case "shopify":
    case "bigblue":
      return triggerIntegration.email ?? undefined
    default:
      return undefined
  }
}

export function getGmailIntegrationIdsFromTriggerIntegration(
  triggerIntegration: z.infer<typeof CampaignTriggerIntegrationSchema> | null | undefined,
) {
  const email = getEmailCampaignChannelIntegration(triggerIntegration)
  const gmail = email?.type === EmailCampaignChannelIntegrationTypeEnum.Gmail ? email : undefined
  if (!gmail) return []

  return [
    ...new Set([
      ...(gmail.gmailIntegrationIds ?? []),
      ...(gmail.gmailIntegrationId ? [gmail.gmailIntegrationId] : []),
    ]),
  ]
}

export function getOutlookIntegrationIdsFromTriggerIntegration(
  triggerIntegration: z.infer<typeof CampaignTriggerIntegrationSchema> | null | undefined,
) {
  const email = getEmailCampaignChannelIntegration(triggerIntegration)
  const outlook =
    email?.type === EmailCampaignChannelIntegrationTypeEnum.Outlook ? email : undefined
  if (!outlook) return []

  return [
    ...new Set([
      ...(outlook.outlookIntegrationIds ?? []),
      ...(outlook.outlookIntegrationId ? [outlook.outlookIntegrationId] : []),
    ]),
  ]
}

export const CampaignStatusEnum = {
  Draft: "Draft",
  Launched: "Launched",
  Paused: "Paused",
  Finished: "Finished",
  Active: "Active",
} as const

export type CampaignStatus = keyof typeof CampaignStatusEnum

export const CampaignLaunchStatusEnum = {
  Scheduled: "Scheduled",
  Launched: "Launched",
  Cancelled: "Cancelled",
} as const

export type CampaignLaunchStatus = keyof typeof CampaignLaunchStatusEnum

export const CampaignLaunchAudienceMatchEnum = {
  All: "all",
  Any: "any",
} as const

export const CampaignLaunchAudienceRuleSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("segments"), segmentIds: z.array(z.string()).min(1) }),
  z.object({ kind: z.literal("tags"), tagIds: z.array(z.string()).min(1) }),
  z.object({
    kind: z.literal("subscribedTo"),
    channels: z.array(z.enum(MarketingChannelEnum)).min(1),
  }),
])

export const CampaignLaunchAudienceSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("import"), importId: z.string().uuid() }),
  z.object({
    kind: z.literal("rules"),
    match: z.enum(CampaignLaunchAudienceMatchEnum),
    include: z.array(CampaignLaunchAudienceRuleSchema).min(1),
    exclude: z.array(CampaignLaunchAudienceRuleSchema),
  }),
])

export type CampaignLaunchAudience = z.infer<typeof CampaignLaunchAudienceSchema>

export const FlowEnum = {
  Inbound: "Inbound",
  Outbound: "Outbound",
} as const

export type Flow = keyof typeof FlowEnum
